import { assertNever, MatchStage, type EnrichmentBatchProgress } from '@signalvote/shared';

/** What one dot in the field shows. Declared in the order the finished field sorts into. */
export enum FieldCell {
  Giving = 'Giving',
  ToConfirm = 'ToConfirm',
  CouldntCheck = 'CouldntCheck',
  NoRecord = 'NoRecord',
  /** In the batch the matcher is working on now. */
  Checking = 'Checking',
  /** Not reached yet. */
  Waiting = 'Waiting',
}

export type FieldRowState = 'done' | 'current' | 'waiting';

export interface FieldRow {
  state: FieldRowState;
  cells: FieldCell[];
}

export interface Field {
  /** How many people each full row stands for: one batch, or several on a long list. */
  peoplePerRow: number;
  rows: FieldRow[];
}

/** The matcher's batch size; the server's `RunChunkSize`. */
export const BATCH_SIZE = 500;
export const FIELD_COLUMNS = 48;
/** Past this many rows the field would be too tall, so each row takes more than one batch. */
const MAX_ROWS = 24;

const OUTCOMES = [FieldCell.Giving, FieldCell.ToConfirm, FieldCell.CouldntCheck] as const;
const ORDER = Object.values(FieldCell);

/** A small seeded shuffle, so a row scatters its colours the same way on every render. */
function shuffle<T>(items: T[], seed: number): T[] {
  let s = seed >>> 0;
  const next = () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [items[i], items[j]] = [items[j], items[i]];
  }
  return items;
}

/**
 * The list as a field of dots, about ten people to a dot, a row per batch of 500 (or per few batches on a
 * long list). A finished row is coloured by what its own batches found; the row being matched shows as
 * being checked; the rest wait. Outcomes too small for a dot in one row carry their remainder to the
 * next, so the field's colours add up to the run's totals.
 */
export function buildField({
  total,
  batches,
  running,
}: {
  total: number;
  batches: EnrichmentBatchProgress[];
  running: boolean;
}): Field {
  const batchCount = Math.ceil(total / BATCH_SIZE);
  const batchesPerRow = Math.max(1, Math.ceil(batchCount / MAX_ROWS));
  const peoplePerRow = BATCH_SIZE * batchesPerRow;
  const rowCount = Math.ceil(total / peoplePerRow);
  const currentRow = running ? Math.floor(batches.length / batchesPerRow) : -1;

  const exact = { [FieldCell.Giving]: 0, [FieldCell.ToConfirm]: 0, [FieldCell.CouldntCheck]: 0 };
  const shown = { ...exact };

  const rows = Array.from({ length: rowCount }, (_, r): FieldRow => {
    const people = Math.min(peoplePerRow, total - r * peoplePerRow);
    const dots = Math.max(1, Math.round((people / peoplePerRow) * FIELD_COLUMNS));
    const finished = batches.slice(r * batchesPerRow, (r + 1) * batchesPerRow);
    const finishedPeople = finished.reduce((sum, b) => sum + b.people, 0);
    const finishedDots = Math.min(dots, Math.round((finishedPeople / people) * dots));
    const scale = dots / people;

    const found: FieldCell[] = [];
    const counts = {
      [FieldCell.Giving]: finished.reduce((sum, b) => sum + b.peopleWithAnyMatch, 0),
      [FieldCell.ToConfirm]: finished.reduce((sum, b) => sum + b.peopleNeedingReview, 0),
      [FieldCell.CouldntCheck]: finished.reduce((sum, b) => sum + b.peopleFailed, 0),
    };
    for (const outcome of OUTCOMES) {
      exact[outcome] += counts[outcome] * scale;
      const n = Math.min(Math.max(0, Math.round(exact[outcome]) - shown[outcome]), finishedDots - found.length);
      shown[outcome] += n;
      found.push(...Array<FieldCell>(n).fill(outcome));
    }
    found.push(...Array<FieldCell>(finishedDots - found.length).fill(FieldCell.NoRecord));

    const state: FieldRowState =
      finishedPeople >= people ? 'done' : r === currentRow ? 'current' : 'waiting';
    const rest = state === 'current' ? FieldCell.Checking : FieldCell.Waiting;
    if (state === 'done') return { state, cells: arrangeLikeSimulation(r, found) };
    return { state, cells: [...shuffle(found, r + 1), ...Array<FieldCell>(dots - finishedDots).fill(rest)] };
  });

  return { peoplePerRow, rows };
}

/** Orders dots by outcome: giving, to confirm, couldn't check, no record. */
export const compareCells = (a: FieldCell, b: FieldCell) => ORDER.indexOf(a) - ORDER.indexOf(b);

/** Every dot in the field, lined up by outcome: how the finished field sorts itself. */
export function sortedCells(field: Field): FieldCell[] {
  return field.rows.flatMap((r) => r.cells).sort(compareCells);
}

/** One pass of the scan line across a row, in seconds. */
export const SCAN_SWEEP = 1.1;
/** How long the unclear dots take to settle one by one under the AI judge, in seconds. */
export const JUDGE_SPAN = 3;

/** A stable pseudo-random number in [0, 1) for a dot and a purpose. */
const noise = (row: number, col: number, salt: number) => {
  const x = Math.sin((row * FIELD_COLUMNS + col) * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * What a dot in the row being matched shows `elapsed` seconds into a step. It's a simulation for the eye,
 * not the batch's data: the matcher only reports a batch's results once it finishes, and then the row
 * takes its real colours. As the scan passes, some dots light green (exact matches, then movers); under
 * the AI judge a scattering of unclear dots settle one at a time. `since` is how long ago it lit.
 */
export function simulatedCell(
  row: number,
  col: number,
  step: MatchStage | null,
  elapsed: number,
): { cell: FieldCell; since: number } {
  const roll = noise(row, col, 1);
  const role = roll < 0.1 ? 'exact' : roll < 0.12 ? 'mover' : roll < 0.24 ? 'unclear' : 'none';
  const passed = elapsed - (col / FIELD_COLUMNS) * SCAN_SWEEP;
  const lit = (cell: FieldCell, since: number) => ({ cell, since });
  const checking = lit(FieldCell.Checking, Infinity);

  switch (step) {
    case MatchStage.Searching:
      return passed >= 0 ? checking : lit(FieldCell.Waiting, Infinity);
    case MatchStage.ExactMatch:
      return role === 'exact' && passed >= 0 ? lit(FieldCell.Giving, passed) : checking;
    case MatchStage.Recovering:
      if (role === 'exact') return lit(FieldCell.Giving, Infinity);
      return role === 'mover' && passed >= 0 ? lit(FieldCell.Giving, passed) : checking;
    case MatchStage.Judging:
    case null: {
      if (role === 'exact' || role === 'mover') return lit(FieldCell.Giving, Infinity);
      if (role !== 'unclear') return checking;
      const settled = step === null ? Infinity : elapsed - noise(row, col, 2) * JUDGE_SPAN;
      if (settled < 0) return checking;
      const verdict = noise(row, col, 3);
      const cell = verdict < 0.55 ? FieldCell.Giving : verdict < 0.75 ? FieldCell.ToConfirm : FieldCell.NoRecord;
      return lit(cell, settled);
    }
    default:
      return assertNever(step);
  }
}

/** Where each step's sweep starts on the row's visual clock, in seconds. */
const VISUAL_START: Record<MatchStage, number> = {
  [MatchStage.Searching]: 0,
  [MatchStage.ExactMatch]: SCAN_SWEEP,
  [MatchStage.Recovering]: 2 * SCAN_SWEEP,
  [MatchStage.Judging]: 3 * SCAN_SWEEP,
};
const VISUAL_STEPS = [MatchStage.Judging, MatchStage.Recovering, MatchStage.ExactMatch, MatchStage.Searching];
/** How far the clock may go while the matcher is really on a step: just short of the next one. */
const visualCap = (step: MatchStage | null) =>
  step === null
    ? 0
    : step === MatchStage.Judging
      ? VISUAL_START[step] + JUDGE_SPAN * 0.9
      : VISUAL_START[step] + SCAN_SWEEP - 0.001;

/**
 * Moves the row's visual clock on by `dt` seconds. The sweeps play at their own steady pace whatever the
 * real steps take (some finish in milliseconds), never past the step the matcher is really on, and at
 * double speed when well behind.
 */
export function advanceVisual(vt: number, dt: number, realStep: MatchStage | null): number {
  const cap = visualCap(realStep);
  if (vt >= cap) return vt;
  return Math.min(cap, vt + dt * (cap - vt > 1.5 ? 2 : 1));
}

/** The step and time into it that the row's visual clock stands at. */
export function visualMoment(vt: number): { step: MatchStage; elapsed: number } {
  const step = VISUAL_STEPS.find((s) => vt >= VISUAL_START[s]) ?? MatchStage.Searching;
  return { step, elapsed: vt - VISUAL_START[step] };
}

/**
 * A finished row's real colours, placed on the dots the simulation lit (its givers first, then those it
 * sent to confirm), so the row barely changes as its batch finishes.
 */
function arrangeLikeSimulation(row: number, cells: FieldCell[]): FieldCell[] {
  const places = cells
    .map((_, col) => ({ col, cell: simulatedCell(row, col, null, Infinity).cell, tie: noise(row, col, 4) }))
    .sort((a, b) => compareCells(a.cell, b.cell) || a.tie - b.tie);
  const out = Array<FieldCell>(cells.length);
  [...cells].sort(compareCells).forEach((cell, k) => (out[places[k].col] = cell));
  return out;
}
